"use client";
import fetchJson from "@/lib/fetchJson";
import useSWR from "swr";
import { useEffect, useState } from "react";
import "blaze-slider/dist/blaze.css";
import Loader from "@/components/CustomUiComponents/Loader";
import "swiper/css";
import {
  Box,
  Flex,
  Heading,
  Switch,
  Text,
  Select,
  Badge,
  Tooltip,
  Button,
} from "@radix-ui/themes";
import { logoUrl } from "@/lib/util";
import { useRouter } from "next/router";
import Link from "next/link";
import useUser from "@/lib/useUser";
import CampaignSearch from "./CampaignSearch";
import CampaignFavoriteButton from "./CampaignFavoriteButton";
import MobileDashboardView from "./DashboardView/MobileDashboardView";
import { IconUserCircle, IconLink } from "@tabler/icons-react";
import MobileAdvancedView from "./AdvancedView/MobileAdvanced";
import Image from "next/image";
import { updateStatus } from "@/services/campaign";
import {
  isMetaReviewEmail,
  isMirroredCampaign,
  MAIN_AGENCY_ID,
} from "@/lib/constants";
import Unauthorized from "../Unauthorized";
import { Permission } from "@/services/permissions";

interface MobileDashboardProps {
  id: string;
  setId: (id: string) => void;
  home?: boolean;
}

const MobileCampaignHeader = ({ id, setId, home }: MobileDashboardProps) => {
  const router = useRouter();
  const { data, mutate, error } = useSWR<Campaign>(
    `/campaign/${id}`,
    fetchJson
  );
  const {
    user,
    canManageUsers,
    canImpersonate,
    canCreateCampaigns,
    canEditCampaigns,
    canViewCampaigns,
    hasPermission,
  } = useUser();
  const [logo, setLogo] = useState("");
  const isAdmin = canManageUsers || canImpersonate;
  const d =
    (canCreateCampaigns || canEditCampaigns) &&
    user?.metadata?.agencyId == data?.agencyId;
  const editable = canImpersonate || d;
  const shareable =
    canImpersonate ||
    (canManageUsers && data?.agencyId == user?.metadata?.agencyId) ||
    ((canCreateCampaigns || canEditCampaigns) &&
      data?.agencyId == user?.metadata?.agencyId) ||
    canViewCampaigns;
  const transferable = canImpersonate;
  let reportable =
    d ||
    (user?.metadata?.campaigns?.includes(parseInt(id)) && canViewCampaigns);
  if (user?.email === "nick@pulsemusicgroup.com") reportable = false;

  // Meta App Review account: force the advanced view (no basic/dashboard).
  const isReviewer = isMetaReviewEmail(user?.email);
  const findView = (home) => {
    if (home) {
      return "dashboard";
    } else {
      return user?.campaignView || "dashboard";
    }
  };
  const [selectedView, setSelectedView] = useState(findView(home));
  useEffect(() => {
    if (isReviewer) setSelectedView("advanced");
  }, [isReviewer]);

  const updateCampaignStatus = async (status: string) => {
    await updateStatus(data.id, status);
    mutate();
  };

  // Check if this campaign is mirrored from another agency
  const isMirrored =
    user?.agencyId &&
    data?.agencyId &&
    isMirroredCampaign(user.agencyId, data.agencyId);

  useEffect(() => {
    setLogo(window.location.host);
  }, []);

  if (!data) return <Loader full={true} />;
  return (
    <>
      <Flex
        id="mobile-searchbar-header"
        direction="row"
        justify="between"
        p="3"
      >
        <div className="w-12 h-10">
          <Image src={logoUrl(logo)} alt="Logo" width={50} height={50} />
        </div>
        <CampaignSearch />
      </Flex>
      {user?.agencyId === MAIN_AGENCY_ID &&
        hasPermission(Permission.CAMPAIGN_CREATE) &&
        data?.id && (
          <Flex px="3" pb="2">
            <Button
              asChild
              variant="outline"
              size="1"
              style={{ width: "100%" }}
            >
              <Link
                href={{
                  pathname: "/social-requests",
                  query: {
                    preset: "trending",
                    campaignId: String(data.id),
                    ...(data.hashtags?.[0]?.hashtag
                      ? { suggestedHashtag: data.hashtags[0].hashtag }
                      : {}),
                  },
                }}
              >
                Boost trending
              </Link>
            </Button>
          </Flex>
        )}
      <Flex id="mobile-campaign-header" gap="3" direction="row" p="5">
        <Box id="mobile-campaign-header-image">
          {(data?.song?.image || data?.artist?.image) && (
            <Image
              width={80}
              height={80}
              className="w-[80px] h-[80px] rounded-2xl object-cover"
              alt="artist"
              unoptimized
              src={`${process.env.NEXT_PUBLIC_DOMAIN}/image-upload/${
                data.song?.image || data.artist?.image
              }`}
            />
          )}
        </Box>
        <Flex
          id="dashboard-campaign-header-text"
          direction="column"
          justify="center"
          py="1"
          gap="2"
        >
          <Box id="dashboard-campaign-name">
            {data && (
              <Flex direction="column" gap="1">
                <Flex direction="row" align="center" gap="2">
                  <Heading size="6">{data?.name}</Heading>
                  <CampaignFavoriteButton campaignId={data.id} size="1" />
                </Flex>
                {isMirrored && (
                  <Tooltip content="This campaign is mirrored from CXC">
                    <Badge
                      color="blue"
                      variant="soft"
                      size="1"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                        fontSize: "10px",
                        padding: "2px 6px",
                        alignSelf: "flex-start",
                      }}
                    >
                      <IconLink size={10} />
                      CXC
                    </Badge>
                  </Tooltip>
                )}
              </Flex>
            )}
          </Box>
          <Flex
            id="dashboard-campaign-subtitle"
            direction="row"
            gap="4"
            align="center"
          >
            {data && (
              <Flex
                id="dashboard-artist-name"
                direction="row"
                gap="1"
                align="center"
              >
                <IconUserCircle className="icon-dark" />
                <Text size="2">{data?.artist?.name}</Text>
              </Flex>
            )}
            <Box id="campaign-status">
              {data && selectedView == "advanced" ? (
                <Select.Root
                  disabled={!editable}
                  size="1"
                  defaultValue={
                    data?.status === "active"
                      ? "active"
                      : data?.status === "draft"
                      ? "draft"
                      : "archived"
                  }
                  onValueChange={(newStatus) => {
                    updateCampaignStatus(newStatus);
                  }}
                >
                  <Select.Trigger />
                  <Select.Content>
                    <Select.Item value="active">
                      <Flex direction="row" align="center">
                        <div className="status-dot-active"></div>
                        <Text size="2">Active</Text>
                      </Flex>
                    </Select.Item>
                    <Select.Item value="archived">
                      <Flex direction="row" align="center">
                        <div className="status-dot-inactive"></div>
                        <Text size="2">Archived</Text>
                      </Flex>
                    </Select.Item>
                    <Select.Item value="draft">
                      <Flex direction="row" align="center">
                        <div className="status-dot-draft"></div>
                        <Text size="2">Draft</Text>
                      </Flex>
                    </Select.Item>
                  </Select.Content>
                </Select.Root>
              ) : (
                <>
                  {data?.status == "active" ? (
                    <Flex direction="row" align="center">
                      <div className="status-dot-active"></div>
                      <Text size="2">Active</Text>
                    </Flex>
                  ) : data?.status == "draft" ? (
                    <Flex direction="row" align="center">
                      <div className="status-dot-draft"></div>
                      <Text size="2">Draft</Text>
                    </Flex>
                  ) : (
                    <Flex direction="row" align="center">
                      <div className="status-dot-inactive"></div>
                      <Text size="2">Archived</Text>
                    </Flex>
                  )}
                </>
              )}
            </Box>
          </Flex>
        </Flex>
      </Flex>
      {/* Reviewer is locked to advanced view, so the toggle is hidden. */}
      {!isReviewer &&
        (isAdmin ||
          [
            "john@creatorxchange.co",
            "Johnmahoney@creatorxchange.co",
            "kal@creatorxchange.co",
            "pvinlaka@creatorxchange.co",
          ].includes(user?.email || "")) && (
        <Flex direction="row" gap="2" align="center" px="5">
          <Switch
            checked={selectedView == "advanced"}
            onCheckedChange={() => {
              setSelectedView(
                selectedView === "dashboard" ? "advanced" : "dashboard"
              );
            }}
          />
          <Flex direction="column">
            <Text size="2">Advanced View</Text>
          </Flex>
        </Flex>
      )}
      {selectedView === "dashboard" ? (
        <MobileDashboardView id={id} setId={setId} />
      ) : (
        <MobileAdvancedView
          editable={editable}
          shareable={shareable}
          reportable={reportable}
          data={data}
          mutate={mutate}
          transferable={transferable}
        />
      )}
    </>
  );
};

export default MobileCampaignHeader;
